import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, Redirect } from 'react-router-dom'
import { theme } from 'theme'
import { createGetRequestsAreLoading } from '../state'
import { STATE_KEYS, setLoading } from '../state/requests'
import {
    getProfileDetail,
    selectIsCampaignsEmpty,
    selectProfileProperties,
    selectUserIsRegistered,
} from 'state/profile'
import { useLocation } from 'react-router-dom'
import { ConstantsActions } from '../state/constants'
import { getToken } from '../services/auth'
import { SnackbarContainer, ToastContainer } from 'particles/Alert/style'
import styled from 'particles/themes/styled'
import PrivateRoute from './PrivateRoute'
import CampaignsList from './CampaignList'
import Campaign from './Campaign'
import Submissions from './Submissions'
import SmallScreenNavBar from './NavBar/SmallScreenNavBar'
import Navbar from './NavBar'
import Loading from './Loading'
import Team from './Team'
import TeamsList from './Team/Teams/TeamsList'
import Roles from './Team/Teams/Roles'
import Tooltips from './Tooltips'
import Banners from './Banners'
import { useIsLoginRoute } from 'hooks'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import PermissionProvider from 'components/Shared/Permission/PermissionProvider'
import { PermissionDenied, PageNotFound } from './Shared/ErrorPage'
import { identifyEvent } from 'config/analytics'
import Auth from './Auth'
import { DraggableBriefModal } from './Submissions/Review/common/BriefRequirements'
import { useRedirect } from 'hooks'
import BusinessDetails from './Team/BusinessDetails'
import AmbassadorsDashboard from './Ambassadors'

export enum ROUTES {
    CampaignsDashboard = '/campaigns',
    SubmissionsDashboard = '/submissions',
    AmbassadorsDashboard = '/ambassadors',
    TeamDashboard = '/teams',
    RoleDashboard = '/teams/:uuid/roles',
    BusinessInformation = '/teams/:uuid/business-details',
    Team = '/team',
    Campaign = '/campaign',
    Banners = '/banners',
}

const authRoutes = {
    Login: '/login',
    LoginExpired: '/login/expired',
    ForgotPassword: '/forgot-password',
    SignUp: '/sign-up',
    ResetPassword: '/reset-password',
    Register: '/register',
} as const
declare global {
    interface Window {
        Cloudinary: any
        cloudinary: any
    }
}
const Container = styled.div`
    background-color: ${theme.newColors.neutral100};
    min-height: calc(100vh - env(safe-area-inset-bottom));
    display: flex;
    @media screen and (max-width: 768px) {
        min-height: -webkit-fill-available;
        max-height: -webkit-fill-available;
        height: calc(100vh - env(safe-area-inset-bottom) - env(safe-area-inset-top));

        #content {
            padding-top: 64px;
        }
    }
    #content {
        max-height: 100vh;
        overflow-y: auto;
        width: 100%;
    }
    #small-navbar {
        width: 100%;
        position: fixed;
        height: fit-content;
        top: 0;
        background-color: ${theme.newColors.neutral100};
        z-index: 110;
        padding: 26px 16px;
    }

    @keyframes snack-bar-fade-out {
        0% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }

    .snack-bar-fade-out {
        animation: snack-bar-fade-out 0.3s ease-out;
    }

    @keyframes snack-bar-fade-in {
        0% {
            opacity: 0;
        }
        100% {
            transform: rotate(0) scale(1);
            opacity: 1;
        }
    }

    .snack-bar-fade-in {
        animation: snack-bar-fade-in 0.3s ease-in;
    }
`
const Root: React.FC = () => {
    const dispatch = useDispatch()
    const token = getToken()
    const location = useLocation()
    const getIsLoading = createGetRequestsAreLoading([STATE_KEYS.Profile, STATE_KEYS.Constants])
    const isLoading = useSelector(getIsLoading)
    const isLoginRoute = useIsLoginRoute()
    const profileProperties = useSelector(selectProfileProperties)
    const isRegistered = useSelector(selectUserIsRegistered)
    const isShowNavBar = Boolean(!isLoginRoute && token && isRegistered)
    const { redirect } = useRedirect()
    const isCampaignsEmpty = useSelector(selectIsCampaignsEmpty)

    useEffect(() => {
        if (token !== null) {
            dispatch(getProfileDetail())
        } else {
            dispatch(setLoading({ stateKey: STATE_KEYS.Profile, isLoading: false }))
        }
        dispatch({ type: ConstantsActions.CONSTANTS_REQUEST })
    }, [dispatch, token, setLoading])

    useEffect(() => {
        localStorage.removeItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        const scriptTag = document.createElement('script')
        const scriptId = 'clouinaryWidget'
        scriptTag.id = scriptId
        scriptTag.src = 'https://widget.cloudinary.com/v2.0/global/all.js'
        scriptTag.async = true
        document.body.appendChild(scriptTag)
        document.getElementById(scriptId)!.addEventListener('onload', () => {
            window.Cloudinary.setCloudName('partipost-staging')
        })
        return () => {
            document.body.removeChild(scriptTag)
        }
    }, [])

    useEffect(() => {
        const exceptionPaths: string[] = [...Object.values(authRoutes).flat(), '/404', '/403']
        if (profileProperties.clientID) {
            identifyEvent(profileProperties)
        }
        if (!isRegistered && profileProperties.clientID && !exceptionPaths.includes(location.pathname)) {
            redirect(`/register?member=${profileProperties.clientID}`)
        }
    }, [profileProperties.clientID, isRegistered, location.pathname])

    if (isLoading) return <Loading />

    return (
        <PermissionProvider>
            <Container>
                {isShowNavBar && <Navbar />}
                {isShowNavBar && <SmallScreenNavBar />}
                <div id="content">
                    <Switch>
                        <Route
                            path={[
                                authRoutes.ForgotPassword,
                                authRoutes.SignUp,
                                authRoutes.ResetPassword,
                                authRoutes.Register,
                                authRoutes.Login,
                            ]}
                            exact
                            component={Auth}
                        />
                        <Route path="/403" exact={true} component={PermissionDenied} />
                        <PrivateRoute
                            path={ROUTES.AmbassadorsDashboard}
                            exact={true}
                            component={AmbassadorsDashboard}
                        />
                        <PrivateRoute path={ROUTES.CampaignsDashboard} exact={true} component={CampaignsList} />
                        <PrivateRoute path={ROUTES.SubmissionsDashboard} component={Submissions} />
                        <PrivateRoute path={ROUTES.Banners} component={Banners} />
                        <PrivateRoute path={ROUTES.Campaign} component={Campaign} />
                        <PrivateRoute path={ROUTES.TeamDashboard} exact component={TeamsList} />
                        <PrivateRoute path={ROUTES.RoleDashboard} exact component={Roles} />
                        <PrivateRoute path={ROUTES.BusinessInformation} exact component={BusinessDetails} />
                        <PrivateRoute path={ROUTES.Team} component={Team} />
                        <PrivateRoute
                            path="/"
                            exact={true}
                            render={() => (
                                <Redirect
                                    to={isCampaignsEmpty ? ROUTES.CampaignsDashboard : ROUTES.SubmissionsDashboard}
                                />
                            )}
                        />
                        <Route path="*" component={PageNotFound} />
                    </Switch>
                </div>

                <DraggableBriefModal />
                <ToastContainer containerId="toast-container" limit={1} position="bottom-center" enableMultiContainer />
                <SnackbarContainer
                    containerId="snackbar-container"
                    limit={1}
                    position="bottom-center"
                    enableMultiContainer
                />
                <Tooltips />
            </Container>
        </PermissionProvider>
    )
}

export default Root
